import {useRef, useState} from "react";
import {css} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {flexDirection, w100} from "@pg-design/helpers-css";
import {form, IFormProps} from "@pg-mono/form";

import {useClickOutside} from "../../hooks/handle_click_outside";
import {DropdownToOpen} from "../constants/DropdownToOpen";
import {Filter} from "./filters/SearchFilter";
import {OfferDistanceSwitch} from "./OfferDistanceSwitch";

interface IOwnProps extends Omit<IFormProps<ISearchFiltersFormValues>, "onSubmit"> {
    handleDistanceSubmit: (userQueryParams: {distance: number}) => void;
    handleDistanceAfterChange: (name: string, value: number, userQueryParams: {distance: number}) => void;
    setDesktopTypeBlendActive: (isOpen: boolean) => void;
    defaultValue?: number;
    value?: number;
    rightDropdown?: boolean;
    topDropdown?: boolean;
}

export interface ISearchFiltersFormValues {
    distance?: number;
}

interface IProps extends IOwnProps {
    isMobileDropdown?: boolean;
    onFilterOpen?: () => void;
}

export const SearchDistanceFilterC = (props: IProps) => {
    const filtersWrapperRef = useRef<HTMLElement | null>(null);
    const filtersWrapperRefHandle = (elem: HTMLElement | null) => {
        filtersWrapperRef.current = elem;
    };

    const [isDistanceFilterOpen, setDistanceFilterOpen] = useState(false);

    useClickOutside(filtersWrapperRef, () => {
        setDistanceFilterOpen(false);
        props.setDesktopTypeBlendActive(false);
    });

    const setOpen = (dropdownToOpen: DropdownToOpen | null) => {
        if (dropdownToOpen) {
            props.onFilterOpen?.();
        }
        setDistanceFilterOpen(!!dropdownToOpen);
        props.setDesktopTypeBlendActive(!!dropdownToOpen);
    };

    const onSubmit = (distanceValue: number) => {
        if (props.handleDistanceSubmit) {
            props.handleDistanceSubmit({distance: distanceValue});
        }
    };

    const onAfterChange = (name: string, value: number, distanceValue: number) => {
        if (props.handleDistanceAfterChange) {
            props.handleDistanceAfterChange(name, value, {distance: distanceValue});
        }
    };

    const distanceProps = props.getFieldProps("distance");
    const distanceValue =
        typeof props.value === "number" ? props.value : typeof distanceProps.value !== "number" ? props.defaultValue || 0 : distanceProps.value;

    useClickOutside(filtersWrapperRef, () => setDistanceFilterOpen(false));

    return (
        <div css={[filterHolder]} ref={filtersWrapperRefHandle}>
            <Filter
                label="Szukaj w promieniu"
                id="distanceFilter"
                {...distanceProps}
                value={distanceValue}
                getLabel={() => `+${distanceValue} km`}
                labelId="distance-filter-field"
                errorOnBottom
                isDropdownOpened={isDistanceFilterOpen}
                setDropdownOpen={setOpen}
                dropdownName={DropdownToOpen.DISTANCE}
                isMobileWidth={false}
                disableClearAction
                isMobileDropdown={props.isMobileDropdown}
                rightDropdown={props.rightDropdown ?? true}
                topDropdown={props.topDropdown ?? false}
                onChange={(name, value) => {
                    if (typeof value === "number") {
                        distanceProps.onChange(name, value);
                    }
                }}
                onAfterChange={(name, value) => {
                    if (typeof value === "number") {
                        distanceProps.onAfterChange?.(name, value);
                    }
                }}
            >
                {(props) => (
                    <>
                        <OfferDistanceSwitch {...props} css={flexDirection("column")} />
                        <Button
                            variant="filled_primary"
                            onClick={() => {
                                props.onChange(props.name, props.value);
                                onAfterChange(props.name, props.value as number, props.value as number);
                                setOpen(null);
                                onSubmit(props.value as number);
                            }}
                            size="small"
                            css={[w100]}
                        >
                            Wybierz
                        </Button>
                    </>
                )}
            </Filter>
        </div>
    );
};

export const SearchDistanceFilter = form<ISearchFiltersFormValues, IProps>(SearchDistanceFilterC);

const filterHolder = css`
    display: flex;
    flex-direction: row;
    align-items: start;
    width: 100%;
    border-right: 0 none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding: 0 0;

    div:nth-of-type(3) {
        margin-right: 0;
    }

    > div {
        margin-right: 0;
    }
`;
